import { gql } from '@apollo/client';
import { CalendarDays, CircleUserRound } from 'lucide-react';
import Link from 'next/link';
import * as React from 'react';
import { Avatar, AvatarFallback } from 'src/shadcn/components/ui/avatar';
import {
  dateToString,
  displayPhoneNumber,
  notEmpty,
  WithoutTypename,
} from 'src/utils/utils';
import { CustomerHoverCardContentFragment } from './__generated__/CustomerHoverCardContent.generated';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { IBrowserHistory } from 'src/services/BrowserHistory/IBrowserHistoryService';
import { RouterLinks } from 'src/routing/routerLinks';

export interface ICustomerHoverCardContentProps {
  data: WithoutTypename<CustomerHoverCardContentFragment> | undefined;
}

export const CustomerHoverCardContent: React.FunctionComponent<
  ICustomerHoverCardContentProps
> = props => {
  const c = props.data;
  if (!c) {
    return null;
  }
  const customerText = [c?.firstName, c?.lastName].filter(notEmpty).join(' ');
  const customerHref = diContainer
    .get<IBrowserHistory>(DI_TYPES.BrowserHistoryService)
    .getUri(RouterLinks.Customers, [c.id]);
  return (
    <div className='flex space-x-4'>
      <Avatar>
        <AvatarFallback>
          <CircleUserRound className='size-6' />
        </AvatarFallback>
      </Avatar>
      <div className='space-y-1'>
        <Link href={customerHref}>
          <h4 className='text-sm font-semibold hover:underline'>
            {customerText}
          </h4>
          {c.companyName && (
            <h4 className='text-sm font-semibold text-muted-foreground'>
              {c.companyName}
            </h4>
          )}
        </Link>
        {c.email && <p className='text-sm'>{c.email}</p>}
        {c.phoneNumber && (
          <p className='text-sm'>{displayPhoneNumber(c.phoneNumber)}</p>
        )}
        {c.secondaryPhoneNumber && (
          <p className='text-sm'>
            {displayPhoneNumber(c.secondaryPhoneNumber)}
          </p>
        )}
        <div className='flex items-center pt-2'>
          <CalendarDays className='mr-2 h-4 w-4 opacity-70' />{' '}
          <span className='text-xs text-muted-foreground'>
            Customer since{' '}
            {dateToString(new Date(c.createdAt), { removeTime: true })}
          </span>
        </div>
      </div>
    </div>
  );
};

const fragment = gql`
  fragment CustomerHoverCardContent on Customer {
    createdAt
    id
    firstName
    lastName
    email
    phoneNumber
    secondaryPhoneNumber
    companyName
  }
`;
